import * as Updates from 'expo-updates';
import { useEffect, useRef } from 'react';
import { AppState, type AppStateStatus } from 'react-native';

/**
 * Only re-check after the app has been backgrounded at least this long, so a
 * quick tab-out (glance at a notification and come right back) doesn't fire a
 * network check on every foreground.
 */
const MIN_BACKGROUND_MS = 5 * 60 * 1000;

/**
 * Keep installed builds on the latest published OTA (EAS Update) bundle.
 *
 * expo-updates already checks and downloads on every cold launch (the default
 * `checkAutomatically: ON_LOAD`), but a user who only backgrounds the app never
 * cold-launches. So this re-checks whenever the app returns to the foreground
 * after being backgrounded ≥5 min, and downloads any newer update. It never
 * reloads: expo-updates launches the downloaded bundle on the next cold start.
 *
 * Why no `Updates.reloadAsync()`: on expo-font 14, an in-process reload drops
 * the runtime-registered brand fonts, and the next <Text> crashes natively in
 * RCTGetFontWeight (expo/expo#50557; Sentry MARKETPRYCE-REWARDS-18/-19). A
 * mid-session restart is also jarring on its own. Revisit once the fonts are
 * embedded natively or expo-font ships the fix (expo/expo#50561).
 *
 * No-op in dev builds / Expo Go / web, where `Updates.isEnabled` is false and
 * the update APIs are unavailable.
 */
export function useOtaUpdates() {
  const backgroundedAt = useRef<number | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!Updates.isEnabled) return;

    async function downloadLatestUpdate() {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const { isAvailable } = await Updates.checkForUpdateAsync();
        if (isAvailable) {
          await Updates.fetchUpdateAsync(); // applied on the next cold start
        }
      } catch {
        // Offline / transient — silently retry on a later foreground.
      } finally {
        inFlight.current = false;
      }
    }

    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'background') {
        backgroundedAt.current = Date.now();
      } else if (state === 'active') {
        const since = backgroundedAt.current;
        backgroundedAt.current = null;
        if (since !== null && Date.now() - since >= MIN_BACKGROUND_MS) {
          void downloadLatestUpdate();
        }
      }
    });
    return () => sub.remove();
  }, []);
}
